import { defineStore } from 'pinia';
import api from '@/services/api';

// Data is considered stale after 5 minutes
const STALE_TIME = 5 * 60 * 1000;

// The mvptk Worker LIST endpoint returns a bare array as the HTTP body (see
// @mvptk/core Application.js — `ctx.body = results` from backend.findAll). axios
// `response.data` is therefore the array itself. We still defensively unwrap a
// `{ data: [...] }` shape to mirror admin-ui's auth-store firstRow() handling.
function asArray(payload) {
	if (Array.isArray(payload)) return payload;
	if (Array.isArray(payload?.data)) return payload.data;
	return [];
}

export const useRestaurantsStore = defineStore('restaurants', {
	state: () => ({
		restaurants: [],
		currentRestaurant: null,
		loading: false,
		error: null,
		lastFetched: null
	}),

	getters: {
		allRestaurants: (state) => state.restaurants,
		restaurantById: (state) => (id) => state.restaurants.find((restaurant) => restaurant.id === id),
		getCurrentRestaurant: (state) => state.currentRestaurant,
		isLoading: (state) => state.loading,
		hasError: (state) => !!state.error,
		getError: (state) => state.error,
		isStale: (state) => !state.lastFetched || Date.now() - state.lastFetched > STALE_TIME,
		hasData: (state) => state.restaurants.length > 0
	},

	actions: {
		setRestaurants(restaurants) {
			this.restaurants = restaurants;
		},

		setCurrentRestaurant(restaurant) {
			this.currentRestaurant = restaurant;
		},

		setLoading(loading) {
			this.loading = loading;
		},

		setError(error) {
			this.error = error;
		},

		addRestaurant(restaurant) {
			this.restaurants.push(restaurant);
		},

		updateRestaurant(updatedRestaurant) {
			const index = this.restaurants.findIndex((restaurant) => restaurant.id === updatedRestaurant.id);
			if (index !== -1) {
				this.restaurants.splice(index, 1, updatedRestaurant);
			}
			if (this.currentRestaurant && this.currentRestaurant.id === updatedRestaurant.id) {
				this.currentRestaurant = updatedRestaurant;
			}
		},

		removeRestaurant(restaurantId) {
			this.restaurants = this.restaurants.filter((restaurant) => restaurant.id !== restaurantId);
			if (this.currentRestaurant && this.currentRestaurant.id === restaurantId) {
				this.currentRestaurant = null;
			}
		},

		async fetchRestaurants(params = {}) {
			this.setLoading(true);
			this.setError(null);

			try {
				const response = await api.getRestaurants(params);
				const restaurants = asArray(response.data);
				this.setRestaurants(restaurants);
				this.lastFetched = Date.now();
				return restaurants;
			} catch (error) {
				this.setError(error.message || 'Failed to fetch restaurants');
				throw error;
			} finally {
				this.setLoading(false);
			}
		},

		// Smart fetch - only fetches if data is missing or stale
		async ensureLoaded(force = false) {
			// Skip if already loading
			if (this.loading) return this.restaurants;

			// Skip if we have fresh data and not forcing
			if (!force && this.hasData && !this.isStale) {
				return this.restaurants;
			}

			return this.fetchRestaurants();
		},

		async fetchRestaurant(restaurantId) {
			this.setLoading(true);
			this.setError(null);

			try {
				const response = await api.getRestaurant(restaurantId);
				this.setCurrentRestaurant(response.data);
				return response.data;
			} catch (error) {
				this.setError(error.message || `Failed to fetch restaurant with ID ${restaurantId}`);
				throw error;
			} finally {
				this.setLoading(false);
			}
		},

		async createRestaurant(restaurantData) {
			this.setLoading(true);
			this.setError(null);

			try {
				const response = await api.createRestaurant(restaurantData);
				const newRestaurant = response.data;
				this.addRestaurant(newRestaurant);
				return newRestaurant;
			} catch (error) {
				this.setError(error.message || 'Failed to create restaurant');
				throw error;
			} finally {
				this.setLoading(false);
			}
		},

		async updateRestaurantAction({ id, restaurantData }) {
			this.setLoading(true);
			this.setError(null);

			try {
				const response = await api.updateRestaurant(id, restaurantData);
				const updatedRestaurant = response.data;
				this.updateRestaurant(updatedRestaurant);
				return updatedRestaurant;
			} catch (error) {
				this.setError(error.message || `Failed to update restaurant with ID ${id}`);
				throw error;
			} finally {
				this.setLoading(false);
			}
		},

		async deleteRestaurant(restaurantId) {
			this.setLoading(true);
			this.setError(null);

			try {
				await api.deleteRestaurant(restaurantId);
				this.removeRestaurant(restaurantId);
			} catch (error) {
				this.setError(error.message || `Failed to delete restaurant with ID ${restaurantId}`);
				throw error;
			} finally {
				this.setLoading(false);
			}
		},

		async fetchCircleRestaurants(circleId) {
			this.setLoading(true);
			this.setError(null);

			try {
				// Use server-side filtering instead of fetching all and filtering client-side
				const response = await api.getRestaurants({ circleId });
				const restaurants = asArray(response.data);
				this.setRestaurants(restaurants);
				return restaurants;
			} catch (error) {
				this.setError(error.message || 'Failed to fetch circle restaurants');
				throw error;
			} finally {
				this.setLoading(false);
			}
		},

		async reorderForCircle(circleId, orderedIds) {
			try {
				await api.reorderRelation('circle', circleId, 'restaurants', orderedIds);
				// Update local sortOrder to match new order
				orderedIds.forEach((id, index) => {
					const restaurant = this.restaurants.find((r) => r.id === id);
					if (restaurant) restaurant.sortOrder = index;
				});
			} catch (error) {
				this.setError(error.message || 'Failed to reorder restaurants');
				throw error;
			}
		}
	}
});
